<script setup>
import { computed, onBeforeUnmount, onMounted, reactive, ref } from "vue";
import { useRoute, useRouter } from "vue-router";
import apiClient from "@/apiClient.js";
import ManualSaveControls from "@/components/ManualSaveControls.vue";
import useManualFormState from "@/composables/useManualFormState";
import SupplierForm from "./SupplierForm.vue";
import { useBreadcrumbStore } from "@/stores/BreadcrumbStore";
import { useAuthStore } from "@/stores/AuthStore";
import dayjs from "dayjs";

const route = useRoute();
const router = useRouter();
const breadcrumbStore = useBreadcrumbStore();
const auth = useAuthStore();
const supplierId = route.params.id;
const supplier = ref(null);
const categories = ref([]);
const form = ref({});
const loading = ref(true);
const deleting = ref(false);
const toast = reactive({
  visible: false,
  message: "",
});
let toastTimeout = null;
const canManageSuppliers = computed(() =>
  auth.hasAnyRole(["finance-department", "super"]),
);
const {
  editable: supplierEditing,
  saving: supplierSaving,
  isDirty: supplierDirty,
  canSave: canSaveSupplier,
  statusText: supplierStatusText,
  statusTone: supplierStatusTone,
  sync: syncSupplierForm,
  startEditing: startEditingSupplier,
  cancelEditing: cancelEditingSupplier,
  startSaving: startSavingSupplier,
  markSaved: markSupplierSaved,
  markSaveFailed: markSupplierSaveFailed,
} = useManualFormState(form, {
  leaveMessage: "You have unsaved supplier changes. Leave this page?",
});

const showToast = (message) => {
  toast.message = message;
  toast.visible = true;
  if (toastTimeout) {
    clearTimeout(toastTimeout);
  }
  toastTimeout = setTimeout(() => {
    toast.visible = false;
  }, 3000);
};

const formatDateTime = (value) => {
  if (!value) {
    return "—";
  }
  return dayjs(value).format("DD MMM YYYY HH:mm");
};

const buildSupplierForm = (value = {}) => ({
  company: value.company ?? "",
  category_id: value.category?.id ?? value.category_id ?? null,
  vat_number: value.vat_number ?? "",
  website: value.website ?? "",
  address: value.address?.data ?? value.address ?? "",
});

const loadCategories = async () => {
  const { data } = await apiClient.get("/api/supplier/categories");
  categories.value = data;
};

const loadSupplier = async () => {
  const { data } = await apiClient.get(`/api/supplier/${supplierId}`);
  supplier.value = data;
  breadcrumbStore.setValue(data.company);
  syncSupplierForm(buildSupplierForm(data));
};

const updateForm = (value) => {
  form.value = value;
};

const saveSupplier = async () => {
  if (!supplierDirty.value) {
    cancelEditingSupplier();
    return;
  }

  startSavingSupplier();
  try {
    const { data } = await apiClient.post(`/api/supplier/${supplierId}`, {
      _method: "PUT",
      ...form.value,
    });
    supplier.value = data;
    breadcrumbStore.setValue(data.company);
    markSupplierSaved(buildSupplierForm(data));
    showToast("Supplier changes saved.");
  } catch (error) {
    markSupplierSaveFailed(
      error?.response?.data?.message ?? "Unable to save supplier.",
    );
  }
};

const deleteSupplier = async () => {
  if (!confirm("Delete this supplier? This can't be undone.")) {
    return;
  }
  deleting.value = true;
  try {
    await apiClient.delete(`/api/supplier/${supplierId}`);
    router.push({
      name: "supplier-list",
    });
  } catch (error) {
    alert(error?.response?.data?.message ?? "Unable to delete supplier.");
  } finally {
    deleting.value = false;
  }
};

onMounted(async () => {
  await Promise.all([loadCategories(), loadSupplier()]);
  loading.value = false;
});

onBeforeUnmount(() => {
  if (toastTimeout) {
    clearTimeout(toastTimeout);
  }
});
</script>

<template>
  <div>
    <form-box v-if="!loading" :header="supplier?.company ?? 'Supplier'">
      <template #nav>
        <button
          v-if="canManageSuppliers"
          class="btn"
          @click.prevent="deleteSupplier"
          :disabled="deleting"
        >
          <span v-if="deleting" class="inline-flex items-center gap-2">
            <span class="inline-block h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white"></span>
            Deleting...
          </span>
          <span v-else>Delete</span>
        </button>
        <ManualSaveControls
          v-if="canManageSuppliers"
          :editing="supplierEditing"
          :saving="supplierSaving"
          :canSave="canSaveSupplier"
          :statusText="supplierStatusText"
          :statusTone="supplierStatusTone"
          @edit="startEditingSupplier"
          @cancel="cancelEditingSupplier"
          @save="saveSupplier"
        />
      </template>
      <SupplierForm :modelValue="form" :categories="categories" :readonly="!canManageSuppliers || !supplierEditing"
        @update:modelValue="updateForm" />
      <div class="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-slate-500 dark:text-slate-400">
        <div>Created: {{ formatDateTime(supplier?.created_at) }}</div>
        <div>Updated: {{ formatDateTime(supplier?.updated_at) }}</div>
      </div>
    </form-box>

    <div v-else class="box animate-pulse h-64"></div>

    <div v-if="toast.visible"
      class="fixed bottom-6 right-6 bg-emerald-600 text-white px-4 py-3 rounded-lg shadow-xl text-sm font-medium flex items-center gap-2">
      <svg class="h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"
        stroke="currentColor">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
          d="M5 13l4 4L19 7" />
      </svg>
      <span>{{ toast.message }}</span>
    </div>
  </div>
</template>
